// src/hooks/useClaimsReadySnapshot.js
//
// Workspace reads that wait for the ID token to carry the workspace.
//
// `firestore.rules` checks `request.auth.token.subscriber_id`. The app's
// `subscriberId` is set the moment a workspace is chosen; the token carries it
// only once set-custom-claims has answered and the token has been refreshed.
// A read made in that gap is refused, and a refused `onSnapshot` is dead:
// Firestore never calls it again. Every reader that starts on its own (the app
// bar, the root providers, the hooks a first page uses) goes through here, so
// that it
//
//  - does not start until the token covers the workspace,
//  - starts again when the token's workspace claim changes, and
//  - when refused anyway, listens again after a short backoff and on the next
//    ID token, logging at debug level while it does. Only a read still refused
//    after that is reported to the caller.
//
// See tokenCoversWorkspace in contexts/AuthContext.js. That function is not
// imported here: dozens of tests replace the AuthContext module with a stub
// that only provides `useAuth`.
import { useCallback, useEffect, useRef, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { isPermissionDenied } from '../utils/firestoreErrors';

// Waits before each new listen after a refusal, in milliseconds. The claim is
// normally in place within a second or two of being refused; a new ID token
// also triggers a listen at once, whatever is left of these.
export const DENIED_RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];

export { isPermissionDenied };

/**
 * Can a member read of workspace `subscriberId` be made with what `auth` says
 * the token carries?
 *
 * A customer-portal token carries the workspace too, but the rules give it no
 * member access (`hasSubscriberAccess` excludes role customer); pass
 * `allowCustomer` for a read the customer rules do allow. A system admin's
 * token passes the workspace rules (`isSystemAdmin()`).
 *
 * An auth value that does not report the token's claims (a test double) is
 * taken at its `claimsReady`, and as ready when it says nothing.
 *
 * `workspaceAccess` says 'ready', 'waiting' (for the claim) or 'never' (no
 * workspace, or a portal token on a member read); `workspaceReadable` is
 * `=== 'ready'`.
 */
export function workspaceAccess(auth, subscriberId, { allowCustomer = false } = {}) {
  if (!subscriberId) return 'never';
  const claims = auth && auth.tokenClaims;
  if (claims && typeof claims === 'object' && 'subscriberId' in claims) {
    if (claims.systemAdmin === true) return 'ready';
    if (claims.subscriberId !== subscriberId) return 'waiting';
    // A portal token for this very workspace will not become a member's.
    if (!allowCustomer && claims.role === 'customer') return 'never';
    return 'ready';
  }
  if (!auth) return 'ready';
  return auth.claimsReady === false ? 'waiting' : 'ready';
}

export function workspaceReadable(auth, subscriberId, options) {
  return workspaceAccess(auth, subscriberId, options) === 'ready';
}

/** A value that changes whenever the token's workspace claim does. */
function claimKeyOf(auth) {
  const claims = auth && auth.tokenClaims;
  if (!claims || typeof claims !== 'object') return '';
  return `${claims.subscriberId || ''}|${claims.role || ''}|${claims.systemAdmin === true}`;
}

/**
 * For a one-shot read (getDoc, getDocs, a count): `ready` says whether to read
 * now, and `claimKey` belongs in the effect's dependencies so the read is made
 * again when the token's workspace claim changes.
 *
 * `subscriberId` defaults to the chosen workspace.
 */
export function useWorkspaceReadable(subscriberId, options = {}) {
  const auth = useAuth() || {};
  const target = subscriberId === undefined ? auth.subscriberId : subscriberId;
  const access = workspaceAccess(auth, target, options);
  return {
    ready: access === 'ready',
    access,
    claimKey: claimKeyOf(auth),
    subscriberId: target || null,
  };
}

/**
 * The retry half, for a listener that cannot be expressed as one target (the
 * task list builds its query imperatively). Put `nonce` in the dependencies of
 * whatever starts the listener; call `onDenied(error)` from its error handler
 * (true: a new listen is scheduled, stay quiet), `onFailed()` for any other
 * failure, and `onSuccess()` on a snapshot.
 *
 * `resetKey` is anything that, when it changes, makes earlier refusals
 * irrelevant (the workspace, the claim). A new ID token after a failure listens
 * again at once.
 */
export function useDeniedRetry({ delays = DENIED_RETRY_DELAYS, resetKey = '' } = {}) {
  const auth = useAuth() || {};
  const issuedAt = (auth.tokenClaims && auth.tokenClaims.issuedAt) || null;
  const [nonce, setNonce] = useState(0);
  const attemptsRef = useRef(0);
  const failedRef = useRef(false);
  const timerRef = useRef(null);
  const delaysRef = useRef(delays);
  delaysRef.current = delays;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // A new workspace or claim starts with a clean slate.
  useEffect(() => {
    clearTimer();
    attemptsRef.current = 0;
    failedRef.current = false;
  }, [resetKey, clearTimer]);

  // A new ID token is the moment a refused read is most likely to succeed.
  const lastIssuedRef = useRef(issuedAt);
  useEffect(() => {
    if (lastIssuedRef.current === issuedAt) return;
    lastIssuedRef.current = issuedAt;
    if (!failedRef.current && !timerRef.current) return;
    clearTimer();
    attemptsRef.current = 0;
    failedRef.current = false;
    setNonce((n) => n + 1);
  }, [issuedAt, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const onDenied = useCallback(() => {
    const list = delaysRef.current || [];
    if (attemptsRef.current >= list.length) {
      failedRef.current = true;
      return false;
    }
    const wait = list[attemptsRef.current];
    attemptsRef.current += 1;
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setNonce((n) => n + 1);
    }, wait);
    return true;
  }, [clearTimer]);

  const onFailed = useCallback(() => {
    failedRef.current = true;
  }, []);

  const onSuccess = useCallback(() => {
    attemptsRef.current = 0;
    failedRef.current = false;
  }, []);

  return { nonce, onDenied, onFailed, onSuccess };
}

/**
 * Listen to a workspace document or query once the token covers the
 * workspace.
 *
 *   useClaimsReadySnapshot(
 *     (sid) => doc(db, 'subscribers', sid),
 *     { onNext: (snap) => ..., onError: (err) => ..., label: 'Subscriber data' },
 *     [someFlag],
 *   );
 *
 * - `makeTarget(subscriberId)` returns the ref or query, or null (or false)
 *   for "do not listen" (a feature that is off). It is called only when the read can be
 *   made.
 * - `onNext(snapshot)` for every snapshot.
 * - `onError(error)` for a failure that is not going to be retried: any error
 *   other than a refusal, or a refusal that outlasted the retries. Without it
 *   the error is logged as a warning with `label`.
 * - `subscriberId` (default: the chosen workspace), `allowCustomer`,
 *   `enabled` (default true), `retryDelays`.
 * - `deps`: further values (primitives) that, when they change, mean
 *   listening again. `makeTarget` and the handlers are read through refs.
 *
 * Returns `{ status, ready }`. `status` is 'idle' (nothing to listen to),
 * 'waiting' (for the claim, or between retries), 'live' or 'error'.
 */
export default function useClaimsReadySnapshot(makeTarget, options = {}, deps = []) {
  const {
    onNext,
    onError,
    label = 'Workspace data',
    subscriberId: explicitSubscriberId,
    allowCustomer = false,
    enabled = true,
    retryDelays = DENIED_RETRY_DELAYS,
  } = options;
  const { access, claimKey, subscriberId } = useWorkspaceReadable(
    explicitSubscriberId,
    { allowCustomer }
  );
  const ready = enabled && access === 'ready';
  const waitingStatus = enabled && access === 'waiting' ? 'waiting' : 'idle';

  const makeTargetRef = useRef(makeTarget);
  makeTargetRef.current = makeTarget;
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const labelRef = useRef(label);
  labelRef.current = label;

  const retry = useDeniedRetry({ delays: retryDelays, resetKey: `${subscriberId}|${claimKey}` });
  const { nonce, onDenied, onFailed, onSuccess } = retry;
  const depsKey = JSON.stringify(deps || []);

  const [status, setStatus] = useState(ready ? 'waiting' : waitingStatus);

  useEffect(() => {
    if (!ready) {
      setStatus(waitingStatus);
      return undefined;
    }
    const target = makeTargetRef.current ? makeTargetRef.current(subscriberId) : null;
    if (target === null || target === false) {
      setStatus('idle');
      return undefined;
    }
    setStatus('waiting');
    let active = true;
    let unsubscribe;
    const handleError = (error) => {
      if (!active) return;
      if (isPermissionDenied(error) && onDenied(error)) {
        // Expected while a claim is landing: listen again shortly.
        console.debug(`${labelRef.current}: read refused, listening again shortly.`, error?.message);
        setStatus('waiting');
        return;
      }
      onFailed();
      setStatus('error');
      if (typeof onErrorRef.current === 'function') {
        onErrorRef.current(error);
      } else {
        console.warn(`${labelRef.current} unavailable:`, error?.message);
      }
    };
    try {
      unsubscribe = onSnapshot(
        target,
        (snapshot) => {
          if (!active) return;
          onSuccess();
          setStatus('live');
          if (typeof onNextRef.current === 'function') onNextRef.current(snapshot);
        },
        handleError
      );
    } catch (error) {
      handleError(error);
    }
    return () => {
      active = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
    // depsKey stands for the caller's deps; the rest is read through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, waitingStatus, subscriberId, claimKey, nonce, depsKey, onDenied, onFailed, onSuccess]);

  return { status, ready };
}
